import API from '../../../store/helpers/api';
import { NIMBLY_API_USER_LOGIN } from '../../../constants/api';
import { storeLoginSession } from '../../../helpers/sessionAuth';

/**
 * The login endpoint's answer, reduced to what the sign-in screen needs.
 *
 * `kind` rather than a nullable token, because the three outcomes call for
 * three different things and collapsing them loses the distinction that
 * matters: a refused password must be shown to the person, while a server we
 * could not reach must not be.
 */
export type CredentialStoreLogin =
  | { kind: 'token'; customToken: string; loginData: unknown }
  | { kind: 'refused'; code: string; message?: string }
  | CredentialStoreUnavailable;
export type CredentialStoreUnavailable = {
  kind: 'unavailable';
  code: 'auth/network-request-failed';
  message: string;
  stage: 'login-api' | 'custom-token-missing' | 'custom-token-exchange';
  cause?: unknown;
};
export type CredentialStoreAuthentication =
  | { kind: 'authenticated' }
  | Exclude<CredentialStoreLogin, { kind: 'token' }>;

/**
 * A Firebase-style error code, so a refusal from our API renders through the
 * same `generateLoginErrorMessage` mapping a Firebase refusal always has.
 * Inventing a new code here would mean every message the screen can show would
 * need a second spelling.
 */
const REFUSAL_CODE = 'auth/wrong-password';
const unavailable = (stage: CredentialStoreUnavailable['stage'], cause?: unknown): CredentialStoreUnavailable => ({
  kind: 'unavailable',
  code: 'auth/network-request-failed',
  message: 'Sign in is temporarily unavailable. Please try again.',
  stage,
  ...(cause === undefined ? {} : { cause })
});

/**
 * Log in against api-users and get back a Firebase custom token.
 *
 * The credential store has proved the password server-side for every
 * organization since 2026-08-25, so asking Firebase to check it again is a
 * second opinion nobody reads. This asks the server instead and uses the token
 * it mints to open the Firebase session the rest of the app still needs - RTDB,
 * Storage and the ID token every API call carries.
 *
 * The identifier is sent exactly as the person typed it. api-users appends
 * `@username.username` for a non-email identifier itself, so doing it here as
 * well would send `name@username.username@username.username`.
 *
 * Never throws. Anything that is not a clear answer becomes `unavailable` so
 * the screen reports a temporary outage. It must not ask Firebase to verify
 * the password: api-users is authoritative, and doing so would silently
 * restore the Phase 4 password fallback this caller is removing.
 */
export async function loginWithCredentialStore(identifier: string, password: string): Promise<CredentialStoreLogin> {
  let response: any;

  try {
    // Unauthenticated: there is no session yet, and this endpoint is
    // gateway-whitelisted precisely because it is the one that creates it.
    const api = new API();
    response = await api.postJSON(NIMBLY_API_USER_LOGIN, { email: identifier, password });
  } catch (error: any) {
    // A 4xx is the server answering, not failing. Anything else - DNS, TLS, a
    // 5xx, a timeout - is us being unable to ask, and must not be reported to
    // the person as a bad password.
    const status = error?.status ?? error?.response?.status;
    if (typeof status === 'number' && status >= 400 && status < 500) {
      return { kind: 'refused', code: REFUSAL_CODE, message: error?.message };
    }
    return unavailable('login-api', error);
  }

  const customToken = response?.data?.customToken;
  if (typeof customToken === 'string' && customToken.length > 0) {
    return { kind: 'token', customToken, loginData: response.data };
  }

  /**
   * A success with no token. The server mints it best-effort and omits it when
   * the mint failed, so this is a working login that simply cannot hand us a
   * Firebase session. This is an outage, not permission to ask Firebase to
   * verify the password independently.
   */
  return unavailable('custom-token-missing');
}

/**
 * Verify through api-users and redeem only the custom token it returns.
 *
 * Keeping the whole flow here makes the security boundary testable: this
 * helper has no Firebase password API dependency, so neither a gateway outage
 * nor a token-exchange failure can turn into a direct password sign-in.
 */
export async function authenticateWithCredentialStore(
  identifier: string,
  password: string,
  signInWithCustomToken: (customToken: string) => Promise<unknown>
): Promise<CredentialStoreAuthentication> {
  const result = await loginWithCredentialStore(identifier, password);
  if (result.kind !== 'token') return result;

  // Before the Firebase sign-in, so the first API call after it already uses the session.
  const finishSessionLogin = storeLoginSession(result.loginData);
  try {
    await signInWithCustomToken(result.customToken);
    finishSessionLogin();
    return { kind: 'authenticated' };
  } catch (error) {
    finishSessionLogin(false);
    return unavailable('custom-token-exchange', error);
  }
}

export default loginWithCredentialStore;
